import React from 'react';
import { Button, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';
import FileSaver from 'file-saver';

import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util';

export interface CalenderExportComponent extends BaseComponent {
  type: 'calenderExport';
  props: CalenderExportProps;
}
export interface CalenderExportProps {
  linkButtonText: string;
  icsItems: ICSFormatItem | ICSFormatItem[];
}

export interface ICSFormatItem {
  startDate: Date;
  endDate: Date;
  subject: string;
  description?: string;
  location?: string;
}

export const CalenderExport: React.FC<CalenderExportProps> = ({
  linkButtonText,
  icsItems,
}) => {
  const convertDate = (date: Date) => {
    var convDate = date.toLocaleDateString().split('.');
    if (convDate.length <= 1) convDate = date.toLocaleDateString().split('/');
    convDate[0] = convDate[0].length == 1 ? '0' + convDate[0] : convDate[0];
    convDate[1] = convDate[1].length == 1 ? '0' + convDate[1] : convDate[1];
    return (
      convDate.reverse().join('') +
      'T' +
      date.toLocaleTimeString('uk-EN').split(':').join('')
    );
  };

  //const convertedStartDate = (startDates instanceof Date ) ? convertDate(startDates) : startDates.map((date) => convertDate(date))
  // const convertedEndDate = (endDates instanceof Date ) ? convertDate(endDates) : endDates.map((date) => convertDate(date))

  const downloadICS = () => {
    var icsFileTextStart =
      'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:evoachGmbH\nMETHOD:PUBLISH';
    var icsFileTextEND = '\nEND:VCALENDAR';
    var icsFileTextData = '';
    const filename = Math.floor(Math.random() * 10000000000);

    if (Array.isArray(icsItems)) {
      icsItems.forEach((element) => {
        const uid = Math.floor(Math.random() * 10000000000);
        const convertedStartDate = convertDate(element.startDate);
        const convertedEndDate = convertDate(element.endDate);
        icsFileTextData =
          icsFileTextData +
          '\nBEGIN:VEVENT\nUID:' +
          uid +
          '\nDTSTART:' +
          convertedStartDate +
          '\nDTEND:' +
          convertedEndDate +
          '\nLOCATION:' +
          element.location +
          '\nSUMMARY:' +
          element.subject +
          '\nDESCRIPTION:' +
          element.description +
          '\nEND:VEVENT';
      });
    } else {
      const uid = Math.floor(Math.random() * 10000000000);
      const convertedStartDate = convertDate(icsItems.startDate);
      const convertedEndDate = convertDate(icsItems.endDate);
      icsFileTextData =
        '\nBEGIN:VEVENT\nUID:' +
        uid +
        '\nDTSTART:' +
        convertedStartDate +
        '\nDTEND:' +
        convertedEndDate +
        '\nLOCATION:' +
        icsItems.location +
        '\nSUMMARY:' +
        icsItems.subject +
        '\nDESCRIPTION:' +
        icsItems.description +
        '\nEND:VEVENT';
    }

    var icsFileText = icsFileTextStart + icsFileTextData + icsFileTextEND;
    var blob = new Blob([icsFileText]);
    FileSaver.saveAs(blob, filename + '.ics');
  };
  return (
    <Button onClick={downloadICS}>
      <Typography>{linkButtonText}</Typography>
    </Button>
  );
};

export const renderCalenderExportAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const dataSource = payload.getValueFrom;
    const entry = dataSource ? context.userData[dataSource] : null;
    let icsItems = payload.icsItems;

    if (dataSource) {
      if (Array.isArray(entry)) {
        if (entry[0].type == 'date') {
          icsItems = entry.map((singleEntry: any) => {
            return {
              startDate: singleEntry.value,
              endDate: singleEntry.value,
              subject: singleEntry.subject ?? singleEntry.title ?? '',
              description: singleEntry.description ?? undefined,
              location: singleEntry.location ?? undefined,
            };
          });
        } else if (typeof entry[0] === 'object') {
          icsItems = entry.map((singleEntry: any) => {
            return {
              startDate: singleEntry.startDate ?? singleEntry.date,
              endDate: singleEntry.endDate ?? singleEntry.date,
              subject: singleEntry.subject ?? singleEntry.value,
              description: singleEntry.description ?? undefined,
              location: singleEntry.location ?? undefined,
            };
          });
        }
      } else if (typeof entry === 'object') {
        if (entry.type == 'date') {
          icsItems = {
            startDate: entry.value,
            endDate: entry.value,
            subject: entry.subject ?? entry.title ?? '',
          };
        } else {
          icsItems = {
            startDate: entry.startDate ?? entry.date,
            endDate: entry.endDate ?? entry.date,
            subject: entry.subject ?? entry.value,
            description: entry.description ?? undefined,
            location: entry.location ?? undefined,
          };
        }
      } else {
        icsItems = {
          ...entry,
          startDate: entry.startDate ?? entry.date,
          endDate: entry.endDate ?? entry.date,
          subject: entry.value ?? '',
        };
      }
    }

    newContext.widgetData.push({
      type: 'calenderExport',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        linkButtonText: translateWithFallback(intl, payload.linkButtonText),
        icsItems: icsItems,
      },
    });
    return newContext;
  });
};
